import {
  Box,
  IconButton,
  Image,
  Stack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { FaChevronRight } from "react-icons/fa";

interface CarouselProps {
  slides: {
    image: string;
    alt: string;
    onClick?: () => void;
  }[];
  displayNextButton?: boolean;
  displayImageStack?: boolean;
  repeat?: boolean;
  sliderImageProps?: React.ComponentProps<typeof Image>;
  slidesPerView?: number;
}

function AdvertisementSlider({
  slides,
  displayNextButton = false,
  displayImageStack = false,
  sliderImageProps = {},
  repeat = false,
  slidesPerView: propSlidesPerView,
}: CarouselProps) {
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const spv = useBreakpointValue({ base: 1, md: 2, lg: 3 }) || 3;
  const slidesPerView = propSlidesPerView || spv;
  const actualSlideCount = slides.length;
  const extendedSlides =
    actualSlideCount > slidesPerView || repeat
      ? [...slides, ...slides, ...slides, ...slides.slice(0, slidesPerView)]
      : slides;
  const maxSlides = Math.max(0, extendedSlides.length - slidesPerView);

  useEffect(() => {
    if (actualSlideCount > slidesPerView) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => {
          if (prev >= maxSlides) {
            return prev - actualSlideCount * 2;
          }
          return prev + 1;
        });
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [actualSlideCount, maxSlides, slidesPerView]);

  const nextSlide = () => {
    setCurrentSlide((prev) => {
      if (prev >= maxSlides) {
        return prev - actualSlideCount * 2;
      }
      return Math.min(prev + 1, maxSlides);
    });
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(Math.min(Math.max(index, 0), maxSlides));
  };

  return (
    <Box position="relative" width="100%" mx="auto" overflow="hidden">
      <Box
        display="flex"
        transition="transform 1s ease-in-out"
        transform={`translateX(-${(currentSlide * 100) / slidesPerView}%)`}
        willChange="transform"
        boxSizing="border-box"
        maxW="100%"
      >
        {extendedSlides.map((slide, index) => (
          <Box
            key={`${slide.image}-${index}`}
            minW={`${100 / slidesPerView}%`}
            maxW={`${100 / slidesPerView}%`}
            flexShrink={0}
            px={2}
            boxSizing="border-box"
          >
            <Image
              src={slide.image}
              alt={slide.alt}
              onClick={slide.onClick}
              width="100%"
              maxW="100%"
              maxH={{
                base: "180px",
                md: "260px",
              }}
              objectFit="cover"
              aspectRatio="16/9"
              rounded="10px"
              {...sliderImageProps}
            />
          </Box>
        ))}
      </Box>

      {displayNextButton && (
        <IconButton
          aria-label="Next slide"
          icon={<FaChevronRight />}
          position="absolute"
          right="10px"
          top="50%"
          transform="translateY(-50%)"
          onClick={nextSlide}
          bg="whiteAlpha.800"
          _hover={{ bg: "whiteAlpha.900" }}
        />
      )}

      {displayImageStack && (
        <Stack
          direction="row"
          spacing={2}
          position="absolute"
          bottom="10px"
          width="full"
          justify="center"
        >
          {Array.from({
            length: Math.min(actualSlideCount, maxSlides + 1),
          }).map((_, index) => (
            <Box
              key={index}
              w="10px"
              h="10px"
              borderRadius="full"
              bg={
                currentSlide % actualSlideCount === index
                  ? "blue.500"
                  : "gray.300"
              }
              cursor="pointer"
              onClick={() => goToSlide(index)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default AdvertisementSlider;
